import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useExternalRecording } from "../../hooks/useExternalRecording";
import "../ExternalWindow/ExternalWindow.css";
import {
  supabaseSchema,
} from "../../../../components/supabaseClient";
import {
  supabaseServiceKey,
  bucketName as defaultAudioBucket,
} from "../../../../components/env";
import {
  normalizeSchemaName,
  resolveRecorderSchema,
} from "../../../../utils/recorderSchema";

/**
 * Opens and manages the standalone external recording popup; exposes imperative API via ref.
 */
export const ExternalRecordingWindowHost = forwardRef(
  (
    {
      state,
      refId,
      onDataChange,
      session,
      popDeps,
      setSession,
      override,
      setShowPersistentComponent,
      condFunc,
      autoSaveSetting = true,
      isTranscriptionModalOpen,
      setIsTranscriptionModalOpen,
      transcriptionData,
      setTranscriptionData,
      onExternalClose,
      onPopupBlocked,
      patientId,
      patientName,
      ehrId,
      supabaseUrl,
      bucketName,
      tableName,
      prompts,
      onAudioSaved,
      shouldOpenImmediately = false,
      liveTranscription = false,
      onRecordingStateChange,
      userRole,
      preferredMicDeviceId,
      userSchemaName,
      aiRecapEnabled,
      copilotEnabled,
      audioBackupIntervalMs,
    },
    ref,
  ) => {
    const windowRef = useRef(null);

    // The row's own schema first — a chart opened through the cross-schema
    // Providers dropdown lives somewhere other than the viewer's home schema,
    // and the recorder writes onto that row. Preferring `supabaseSchema` here
    // aimed every such write at a uuid the home schema doesn't have. Otherwise
    // the order is unchanged: supabaseSchema, then userSchemaName.
    const effectiveSchema =
      resolveRecorderSchema({ state, homeSchema: supabaseSchema }) ||
      normalizeSchemaName(userSchemaName) ||
      null;

    const patientAudioNumber = state?.more_audios?.length
      ? state.more_audios.length + 1
      : 1;

    console.log("🎤 [EXTERNAL-PAGE] Schema for external window:", {
      supabaseSchema,
      userSchemaName,
      effectiveSchema,
      patientAudioNumber,
    });

    const { generateExternalHTML, getDynamicWindowSize } = useExternalRecording(
      {
        state,
        refId,
        patientId,
        patientName,
        ehrId,
        liveTranscription,
        userRole,
        preferredMicDeviceId,
        aiRecapDefault: aiRecapEnabled,
        copilotEnabled,
        audioBackupIntervalMs,
        supabaseServiceKey: supabaseServiceKey,
        supabaseUrl,
        audioBucketName: defaultAudioBucket || "audio",
        tableName,
        dbSchemaName: effectiveSchema,
        audioNumber: patientAudioNumber,
        userId: session?.user?.id,
        userJwtToken: session?.access_token,
      },
    );

    const openExternalWindow = React.useCallback(() => {
      try {
        console.log("Attempting to open external recording window...");

        const htmlContent = generateExternalHTML();
        const { width, height } = getDynamicWindowSize();

        console.log(`Opening window with size: ${width}x${height}`);

        const newWindow = window.open(
          "",
          "externalRecording",
          `width=${width},height=${height},scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no,titlebar=yes,top=0,left=0`,
        );

        if (newWindow) {
          console.log("External window opened successfully");
          newWindow.document.write(htmlContent);
          newWindow.document.close();
          windowRef.current = newWindow;

          const checkClosed = setInterval(() => {
            if (newWindow.closed) {
              clearInterval(checkClosed);
              if (onExternalClose) {
                onExternalClose();
              }
            }
          }, 1000);

          return true;
        }
        console.log("Window.open returned null - popup blocked");
        if (onPopupBlocked) {
          onPopupBlocked();
        } else if (onExternalClose) {
          onExternalClose();
        }
        return false;
      } catch (error) {
        console.error("Error opening external window:", error);
        if (onPopupBlocked) {
          onPopupBlocked();
        } else if (onExternalClose) {
          onExternalClose();
        }
        return false;
      }
    }, [
      generateExternalHTML,
      getDynamicWindowSize,
      onExternalClose,
      onPopupBlocked,
    ]);

    const cleanupTemporaryBackups = React.useCallback(async () => {
      try {
        console.log("Cleaning up temporary backups...");
        if (windowRef.current && !windowRef.current.closed) {
          windowRef.current.close();
          windowRef.current = null;
        }
        console.log("Temporary backups cleanup completed");
        return true;
      } catch (error) {
        console.error("Error cleaning up temporary backups:", error);
        return false;
      }
    }, []);

    useImperativeHandle(ref, () => ({
      openWindow: openExternalWindow,
      focusWindow: () => {
        if (windowRef.current && !windowRef.current.closed) {
          windowRef.current.focus();
          return true;
        }
        return false;
      },
      sendMessage: (data) => {
        if (windowRef.current && !windowRef.current.closed) {
          windowRef.current.postMessage(data, "*");
          return true;
        }
        return false;
      },
      cleanupTemporaryBackups,
    }));

    useEffect(() => {
      if (shouldOpenImmediately && !windowRef.current) {
        console.log(
          "ExternalRecordingPage: Opening external window immediately (useEffect)...",
        );
        const success = openExternalWindow();
        if (!success) {
          console.error(
            "Failed to open external recording window - popup likely blocked",
          );
        }
      }
    }, [shouldOpenImmediately, openExternalWindow]);

    return null;
  },
);

ExternalRecordingWindowHost.displayName = "ExternalRecordingWindowHost";
